import * as React from 'react';
import {
  Check,
  ChevronDown,
  ImagePlus,
  Loader2,
  Plus,
  Trash2,
  Users,
  X,
} from 'lucide-react';
import type { Agent, AgentCreateInput } from '@proptr/shared';
import { EmptyState } from '../components/EmptyState.js';
import {
  HeaderFilter,
  HeaderLabel,
  SortHeader,
  TableCard,
  TableHeaderRow,
  TableLoading,
  TableMessage,
  TablePagination,
} from '../components/table/index.js';
import { AppShell } from '../components/layout/AppShell.js';
import { StatusBadge } from '../components/StatusBadge.js';
import { Tooltip } from '../components/ui/tooltip.js';
import { isTestMode } from '../lib/testMode.js';
import { Button } from '../components/ui/button.js';
import { Card } from '../components/ui/card.js';
import { Input } from '../components/ui/input.js';
import { Label } from '../components/ui/label.js';
import { uploadImage } from '../lib/uploadImage.js';
import { cn, initials } from '../lib/utils.js';
import { client } from '../main.js';

type StatusFilter = 'active' | 'disabled' | 'all';
type SortKey = 'name' | 'totalLeads' | 'totalListings' | 'createdAt';
type SortOrder = 'asc' | 'desc';

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'active', label: 'Active agents' },
  { value: 'disabled', label: 'Disabled agents' },
  { value: 'all', label: 'All agents' },
];

export function AgentsPage() {
  const [status, setStatus] = React.useState<StatusFilter>('active');
  const [sortBy, setSortBy] = React.useState<SortKey>('name');
  const [order, setOrder] = React.useState<SortOrder>('asc');
  const [pageSize, setPageSize] = React.useState<number>(25);
  const [page, setPage] = React.useState<number>(1);

  const [items, setItems] = React.useState<Agent[] | null>(null);
  const [meta, setMeta] = React.useState<{
    total: number;
    active: number;
    disabled: number;
    pages: number;
  } | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  // True while a filter/sort/page change refetches with rows
  // already on screen — drives TableCard's dim + "Updating" pill.
  const [refreshing, setRefreshing] = React.useState(false);
  // Bumped after a successful create to retrigger the list-load effect.
  const [reloadTick, setReloadTick] = React.useState(0);

  // Create-agent modal state.
  const [createOpen, setCreateOpen] = React.useState(false);
  const [creating, setCreating] = React.useState(false);
  const [createError, setCreateError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let alive = true;
    setError(null);
    setRefreshing(true);
    client.agents
      .list({ status, sort: sortBy, order, page, limit: pageSize })
      .then((res) => {
        if (!alive) return;
        setItems(res.items);
        setMeta({
          total: res.meta.total,
          active: res.meta.active,
          disabled: res.meta.disabled,
          pages: res.meta.pages,
        });
      })
      .catch((err: Error) => {
        if (alive) setError(err.message);
      })
      .finally(() => {
        if (alive) setRefreshing(false);
      });
    return () => {
      alive = false;
    };
  }, [status, sortBy, order, page, pageSize, reloadTick]);

  const handleCreate = async (input: AgentCreateInput) => {
    if (creating) return;
    setCreateError(null);
    setCreating(true);
    try {
      await client.agents.create(input);
      setCreateOpen(false);
      setReloadTick((t) => t + 1);
    } catch (err) {
      setCreateError((err as Error).message || 'Could not create agent.');
    } finally {
      setCreating(false);
    }
  };

  const handleSort = (key: SortKey) => {
    if (sortBy === key) {
      setOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setOrder('desc');
    }
  };

  const total = meta?.total ?? 0;
  const totalPages = meta?.pages ?? 1;

  return (
    <AppShell>
      <div className="flex flex-1 flex-col space-y-6">
        {/* Page header */}
        <div className="flex flex-wrap items-start justify-between gap-6">
          <div className="min-w-0">
            <h1 className="font-display text-[40px] font-medium leading-none tracking-[-0.02em]">
              Agents
            </h1>
            <p className="mt-3 font-mono text-[11px] text-muted-foreground">
              <span className="text-foreground">
                {meta ? meta.active + meta.disabled : '—'}
              </span>{' '}
              agents in organization
              {meta && (
                <>
                  {' · '}
                  <span className="text-foreground">{meta.active}</span> active
                  {' · '}
                  <span className="text-foreground">{meta.disabled}</span> disabled
                </>
              )}
            </p>
          </div>

          <NewAgentCard
            onCreate={() => {
              setCreateError(null);
              setCreateOpen(true);
            }}
          />
        </div>

        {/* Agents card */}
        <TableCard
          minWidth={1040}
          busy={refreshing && items !== null}
          footer={
            <TablePagination
              page={page}
              pageSize={pageSize}
              total={total}
              totalPages={totalPages}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(1);
              }}
            />
          }
        >
          <TableHeaderRow>
                {/* min-w folds in the rows' chevron+avatar columns
                    (32+40+two 16px gaps) so labels still align while the
                    filter chip sits flush left on every page. */}
                <HeaderFilter
                  className="min-w-[324px] flex-[3_1_324px]"
                  value={status}
                  options={STATUS_OPTIONS}
                  onChange={(v) => {
                    setStatus(v);
                    setPage(1);
                  }}
                />
                <HeaderLabel className="min-w-[120px] flex-[1.5_1_120px]">
                  Proptree
                </HeaderLabel>
                <HeaderLabel className="min-w-[140px] flex-[1.8_1_140px]">
                  Phone
                </HeaderLabel>
                <HeaderLabel className="min-w-[100px] flex-[1_1_100px]">
                  Permit
                </HeaderLabel>
                <SortHeader
                  label="Listings"
                  sortKey="totalListings"
                  sortBy={sortBy}
                  order={order}
                  onSort={handleSort}
                  className="min-w-[80px] flex-[1_1_80px]"
                />
                <SortHeader
                  label="Leads"
                  sortKey="totalLeads"
                  sortBy={sortBy}
                  order={order}
                  onSort={handleSort}
                  className="min-w-[70px] flex-[1_1_70px]"
                />
                <SortHeader
                  label="Created"
                  sortKey="createdAt"
                  sortBy={sortBy}
                  order={order}
                  onSort={handleSort}
                  className="min-w-[110px] flex-[1.5_1_110px]"
                />
                <HeaderLabel className="min-w-[128px] flex-[1.3_1_128px]">
                  Source
                </HeaderLabel>
          </TableHeaderRow>

              {error ? (
                <TableMessage tone="destructive">{error}</TableMessage>
              ) : items === null ? (
                <TableLoading />
              ) : items.length === 0 ? (
                <EmptyState
                  icon={Users}
                  title="No agents found"
                  description="No agents match the selected filters. Add agents — or connect your CRM and they sync in automatically — and your team shows up here."
                />
              ) : (
                items.map((agent) => <AgentRow key={agent._id} agent={agent} />)
              )}
        </TableCard>
      </div>

      {createOpen && (
        <CreateAgentDialog
          onClose={() => setCreateOpen(false)}
          onSubmit={handleCreate}
          creating={creating}
          error={createError}
        />
      )}
    </AppShell>
  );
}

function AgentRow({ agent }: { agent: Agent }) {
  const [open, setOpen] = React.useState(false);
  const createdAt = agent.createdAt
    ? formatDate(new Date(agent.createdAt as string))
    : '—';
  return (
    <div className="border-b border-[hsl(var(--hairline))] last:border-b-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-4 px-5 py-4 text-left transition-colors hover:bg-muted/60"
      >
        <span
          className={cn(
            'flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-border text-muted-foreground transition-transform',
            open && 'rotate-180 bg-muted text-foreground',
          )}
        >
          <ChevronDown className="h-3.5 w-3.5" />
        </span>
        <Avatar name={agent.name ?? ''} image={agent.image ?? undefined} />
        <div className="min-w-[220px] flex-[3_1_220px]">
          <div className="truncate font-sans text-[14px] font-medium text-foreground">
            {agent.name || '—'}
          </div>
          <div className="truncate font-mono text-[11px] text-muted-foreground">
            {agent.email || (agent.proptree?.alias ? `proptr.ee/${agent.proptree.alias}` : '—')}
          </div>
        </div>
        <ProptreeCell hasProptree={agent.proptree != null} />
        <Cell value={agent.phone || '—'} className="min-w-[140px] flex-[1.8_1_140px]" />
        <Cell value={agent.license || '—'} className="min-w-[100px] flex-[1_1_100px]" />
        <Cell
          value={String(agent.totalListings)}
          className="min-w-[80px] flex-[1_1_80px]"
          dim={agent.totalListings === 0}
        />
        <Cell
          value={String(agent.totalLeads)}
          className="min-w-[70px] flex-[1_1_70px]"
          dim={agent.totalLeads === 0}
        />
        <Cell value={createdAt} className="min-w-[110px] flex-[1.5_1_110px]" />
        <div className="flex min-w-[128px] flex-[1.3_1_128px] justify-start">
          <SourceChip source={agent.source} />
        </div>
      </button>

      {open && <AgentDetail agent={agent} />}
    </div>
  );
}

function AgentDetail({ agent }: { agent: Agent }) {
  const statusLabel = agent.status?.name || (agent.status?.id === 1 ? 'Active' : 'Disabled');
  const proptreeUrl = agent.proptree?.alias
    ? `proptr.ee/${agent.proptree.alias}`
    : '—';
  return (
    <div className="border-t border-[hsl(var(--hairline))] bg-muted/40 px-5 py-5">
      <div className="grid grid-cols-2 gap-x-8 gap-y-4 md:grid-cols-4">
        <DetailRow label="Status" value={statusLabel} />
        <DetailRow label="Email" value={agent.email || '—'} />
        <DetailRow label="Phone" value={agent.phone || '—'} />
        <DetailRow label="Permit" value={agent.license || '—'} />
        <DetailRow label="Proptree" value={proptreeUrl} />
        <DetailRow label="Listings" value={String(agent.totalListings)} />
        <DetailRow
          label="Leads"
          value={String(agent.totalLeads)}
          highlight={agent.totalLeads > 0}
        />
        <DetailRow label="ID" value={agent._id} />
      </div>
    </div>
  );
}

function Avatar({ name, image }: { name: string; image?: string }) {
  if (image) {
    return (
      <img
        src={image}
        alt={name}
        className="h-10 w-10 shrink-0 rounded-full bg-muted object-cover"
      />
    );
  }
  return (
    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted font-mono text-[11px] font-bold uppercase">
      {initials(name || '?')}
    </div>
  );
}

function Cell({
  value,
  className,
  dim,
}: {
  value: string;
  className?: string;
  dim?: boolean;
}) {
  return (
    <div className={cn('text-left', className)}>
      <div
        className={cn(
          'truncate font-mono text-[12px] font-medium tabular-nums text-foreground/80',
          dim && 'text-muted-foreground',
        )}
      >
        {value}
      </div>
    </div>
  );
}

function ProptreeCell({ hasProptree }: { hasProptree: boolean }) {
  return (
    <div className="flex min-w-[120px] flex-[1.5_1_120px] justify-start">
      <StatusBadge
        label={hasProptree ? 'Active' : 'No proptree'}
        tone={hasProptree ? 'success' : 'muted'}
      />
    </div>
  );
}


function DetailRow({
  label,
  value,
  highlight,
}: {
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <div>
      <div className="mono-label text-muted-foreground">{label}</div>
      <div
        className={cn(
          'mt-1 truncate font-mono text-[13px] font-semibold tabular-nums',
          highlight && 'text-primary',
        )}
      >
        {value}
      </div>
    </div>
  );
}

function formatDate(d: Date): string {
  if (Number.isNaN(d.getTime())) return '—';
  return d.toISOString().slice(0, 10);
}

// Manual = added by the manager from the "New agent" modal.
// Auto-created = pulled in by the listing-sync parser. Legacy rows
// without a source value display as Auto-created (the legacy parser
// created everything).
function SourceChip({ source }: { source?: Agent['source'] }) {
  if (source === 'manual') {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/10 px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-[0.06em] text-primary">
        <span className="h-1.5 w-1.5 rounded-full bg-primary" />
        Manually created
      </span>
    );
  }
  // sync / system / missing → "Auto-created" (system would be hidden
  // by the isDefaultAgent filter, but we treat it the same here for
  // defence in depth).
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/30 px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-[0.06em] text-muted-foreground">
      <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/60" />
      Auto-created
    </span>
  );
}

// Compact "Create new agent" card shown next to the page title.
// Mirrors NewFormCard on the Forms page. No quota for agents today.
// Creation is locked while Testing mode is on — POSTs are not intercepted
// by the fixture layer, so the agent would be created for real.
function NewAgentCard({ onCreate }: { onCreate: () => void }) {
  const testMode = isTestMode();
  const createButton = (
    <Button size="sm" variant="outline" onClick={onCreate} disabled={testMode}>
      <Plus />
      Create
    </Button>
  );
  return (
    <Card className="flex w-[380px] items-center gap-3 px-4 py-3">
      <div className="min-w-0 flex-1">
        <div className="font-sans text-[13px] font-semibold leading-tight text-foreground">
          New agent
        </div>
        <p className="mt-1 font-sans text-[11px] leading-snug text-muted-foreground">
          Add an agent manually — they'll get a profile, no CRM feed needed.
        </p>
      </div>
      {testMode ? (
        <Tooltip
          label="Can't create agent in test mode"
          className="cursor-not-allowed"
        >
          {createButton}
        </Tooltip>
      ) : (
        createButton
      )}
    </Card>
  );
}

// Inline modal — no Radix dialog component in components/ui yet, so we
// roll a small fixed-position overlay with Esc-to-close + click-outside-
// to-close. Esc/click only fire when not submitting so the form doesn't
// vanish mid-save.
function CreateAgentDialog({
  onClose,
  onSubmit,
  creating,
  error,
}: {
  onClose: () => void;
  onSubmit: (input: AgentCreateInput) => void | Promise<void>;
  creating: boolean;
  error: string | null;
}) {
  const [name, setName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [phone, setPhone] = React.useState('');
  const [license, setLicense] = React.useState('');
  const [image, setImage] = React.useState('');
  const [uploadingImage, setUploadingImage] = React.useState(false);
  const [imageError, setImageError] = React.useState<string | null>(null);
  const firstInputRef = React.useRef<HTMLInputElement>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const handleImageFile = async (file: File) => {
    setUploadingImage(true);
    setImageError(null);
    try {
      const url = await uploadImage({ file, kind: 'agent-avatar' });
      setImage(url);
    } catch (e) {
      setImageError((e as Error).message || 'Upload failed.');
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const clearImage = () => {
    setImage('');
    setImageError(null);
  };

  React.useEffect(() => {
    firstInputRef.current?.focus();
  }, []);
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !creating) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, creating]);

  const submitDisabled =
    creating || uploadingImage || name.trim().length < 2 || !email.trim();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (submitDisabled) return;
    const input: AgentCreateInput = {
      name: name.trim(),
      email: email.trim().toLowerCase(),
    };
    if (phone.trim()) input.phone = phone.trim();
    if (license.trim()) input.license = license.trim();
    if (image.trim()) input.image = image.trim();
    void onSubmit(input);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={() => {
        if (!creating) onClose();
      }}
    >
      <Card
        className="w-full max-w-[440px] p-0 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <form onSubmit={handleSubmit}>
          <div className="flex items-start justify-between gap-3 border-b border-[hsl(var(--hairline))] px-5 pb-3 pt-4">
            <div>
              <div className="font-sans text-[15px] font-semibold text-foreground">
                New agent
              </div>
              <p className="mt-0.5 font-mono text-[11px] text-muted-foreground">
                Email is the unique key per brokerage.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              disabled={creating}
              aria-label="Close"
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground disabled:opacity-50"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="space-y-4 px-5 py-4">
            <div className="space-y-1.5">
              <Label>Profile image</Label>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void handleImageFile(f);
                }}
              />
              <div className="flex items-center gap-3 rounded-md border border-border bg-muted/30 px-3 py-2.5">
                {image ? (
                  <img
                    src={image}
                    alt="Profile preview"
                    className="h-12 w-12 shrink-0 rounded-full bg-muted object-cover"
                  />
                ) : (
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
                    <ImagePlus className="h-4 w-4" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="font-sans text-[12px] font-medium text-foreground">
                    {image ? 'Image uploaded' : 'No image selected'}
                  </div>
                  <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.04em] text-muted-foreground">
                    JPEG, PNG, or WebP · optional
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-1.5">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={creating || uploadingImage}
                  >
                    {uploadingImage ? (
                      <Loader2 className="animate-spin" />
                    ) : (
                      <ImagePlus />
                    )}
                    {uploadingImage
                      ? 'Uploading…'
                      : image
                        ? 'Replace'
                        : 'Upload'}
                  </Button>
                  {image && !uploadingImage && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={clearImage}
                      disabled={creating}
                      aria-label="Remove image"
                    >
                      <Trash2 />
                    </Button>
                  )}
                </div>
              </div>
              {imageError && (
                <div className="font-mono text-[11px] text-destructive">
                  {imageError}
                </div>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="agent-name">Name</Label>
              <Input
                id="agent-name"
                ref={firstInputRef}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Jane Doe"
                required
                disabled={creating}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="agent-email">Email</Label>
              <Input
                id="agent-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="jane@yourbrokerage.com"
                required
                disabled={creating}
                autoComplete="off"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="agent-phone">Phone</Label>
                <Input
                  id="agent-phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+971 50 …"
                  disabled={creating}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="agent-license">License</Label>
                <Input
                  id="agent-license"
                  value={license}
                  onChange={(e) => setLicense(e.target.value)}
                  placeholder="RERA / BRN"
                  disabled={creating}
                />
              </div>
            </div>

            {error && (
              <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 font-mono text-[12px] text-destructive">
                {error}
              </div>
            )}
          </div>

          <div className="flex items-center justify-end gap-2 border-t border-[hsl(var(--hairline))] px-5 py-3">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onClose}
              disabled={creating}
            >
              Cancel
            </Button>
            <Button type="submit" size="sm" disabled={submitDisabled}>
              {creating ? <Loader2 className="animate-spin" /> : <Check />}
              {creating ? 'Creating…' : 'Create agent'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
